import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { decodeHtmlEntities } from '@theme/airbag-decode-html-entities';
import { fetchAirbagProductDetails } from '@theme/airbag-product-details-service';

/**
 * Legacy storefront parity: default intro embed + FittingVideos / InActionVideos (iFrameUrl) +
 * optional InstallationVideos + pressure/height YouTube extras when global metafields exist.
 *
 * @typedef {{ href: string; title: string; subtitle?: string; description?: string }} AirbagVideoEntry
 *
 * @typedef {Object} AirbagInstallVideoRefs
 * @property {HTMLElement} skeleton
 * @property {HTMLElement} panel
 * @property {HTMLElement} tablist
 * @property {HTMLElement} detail
 * @property {HTMLElement} detailFrameWrap
 * @property {HTMLIFrameElement} detailFrame
 * @property {HTMLAnchorElement} detailLink
 * @property {HTMLTemplateElement} arrowIconTemplate
 *
 * @extends {Component<AirbagInstallVideoRefs>}
 */
class AirbagInstallVideos extends Component {
  #requestId = 0;
  /** @type {AirbagVideoEntry[]} */
  #entries = [];
  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.refs.tablist?.addEventListener('keydown', this.#onTablistKeydown);
    this.#desktopMq = window.matchMedia('(min-width: 750px)');
    this.#desktopMq.addEventListener('change', this.#syncTablistOrientation);
    this.#syncTablistOrientation();
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.refs.tablist?.removeEventListener('keydown', this.#onTablistKeydown);
    this.#desktopMq?.removeEventListener('change', this.#syncTablistOrientation);
    this.#requestId += 1;
    super.disconnectedCallback();
  }

  #syncTablistOrientation = () => {
    const tablist = this.refs.tablist;
    if (!tablist) return;
    tablist.setAttribute('aria-orientation', this.#desktopMq?.matches ? 'vertical' : 'horizontal');
  };

  /**
   * @param {Event & { detail?: { resource?: unknown; data?: { productId?: string | number } } }} event
   */
  #onVariantUpdate = (event) => {
    const source = this.dataset.productNrSource;
    if (source !== 'sku' && source !== 'barcode') return;
    const detail = event.detail;
    const expectedPid = this.dataset.productId;
    const evtPid = detail?.data?.productId;
    if (evtPid != null && expectedPid != null && String(evtPid) !== String(expectedPid)) return;

    const resource = detail?.resource;
    if (!resource || typeof resource !== 'object') return;

    const variant = /** @type {{ sku?: string; barcode?: string }} */ (resource);
    this.dataset.initialProductNr =
      source === 'sku' ? String(variant.sku ?? '') : String(variant.barcode ?? '');
    this.#load();
  };

  #currentNr() {
    return (this.dataset.initialProductNr || '').trim();
  }

  /** @returns {HTMLElement | null} */
  #getShopifySection() {
    const el = this.closest('.shopify-section');
    return el instanceof HTMLElement ? el : null;
  }

  #showOuterSection() {
    const section = this.#getShopifySection();
    if (section) section.hidden = false;
  }

  /**
   * Hide the whole theme section (background + content) when there is nothing to show.
   * @param {string} _reason
   */
  #hideOuterSection(_reason) {
    this.refs.skeleton.hidden = true;
    this.refs.panel.hidden = true;
    this.setAttribute('aria-busy', 'false');
    const section = this.#getShopifySection();
    if (section) section.hidden = true;
  }

  /**
   * @param {unknown} raw
   * @returns {unknown[]}
   */
  #normalizeArray(raw) {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    return [raw];
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #str(value) {
    if (value == null) return '';
    return String(value).trim();
  }

  /**
   * @param {Record<string, unknown>} item
   */
  #getVideoHref(item) {
    return this.#str(
      item.iFrameUrl ??
        item.IFrameUrl ??
        item.iframeUrl ??
        item.Url ??
        item.url ??
        item.Link ??
        item.href ??
        item.EmbedUrl ??
        item.YouTubeUrl ??
        item.VideoUrl,
    );
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #youtubeEmbed(href) {
    const id =
      href.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/)?.[1] ?? '';
    if (!id) return '';
    return `https://www.youtube.com/embed/${id}`;
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #vimeoEmbed(href) {
    const id = href.match(/vimeo\.com\/(?:video\/)?(\d{5,})/)?.[1] ?? '';
    if (!id) return '';
    return `https://player.vimeo.com/video/${id}`;
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #resolveIframeSrc(href) {
    const h = href.trim();
    if (
      /^https?:\/\/(www\.)?youtube\.com\/embed\//i.test(h) ||
      /^https?:\/\/player\.vimeo\.com\/video\//i.test(h)
    ) {
      return h;
    }
    if (/^\/\//.test(h)) {
      return `https:${h}`;
    }
    return this.#youtubeEmbed(h) || this.#vimeoEmbed(h);
  }

  /**
   * @param {AirbagVideoEntry[]} entries
   * @param {unknown} rows
   * @param {string} subtitleLabel
   */
  #pushApiVideos(entries, rows, subtitleLabel) {
    const sub = decodeHtmlEntities(subtitleLabel) || undefined;
    for (const raw of this.#normalizeArray(rows)) {
      if (!raw || typeof raw !== 'object') continue;
      const item = /** @type {Record<string, unknown>} */ (raw);
      const href = this.#getVideoHref(item);
      if (!href) continue;
      const apiTitle = this.#str(item.Title ?? item.Name ?? item.Label ?? item.Description);
      const title =
        decodeHtmlEntities(apiTitle) ||
        decodeHtmlEntities(this.dataset.labelTitleFallback) ||
        href;
      const description = decodeHtmlEntities(
        this.#str(item.LongDescription ?? item.Description2 ?? item.Notes ?? item.Body ?? ''),
      );
      entries.push({ href, title, subtitle: sub, description: description || undefined });
    }
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {AirbagVideoEntry[]}
   */
  #buildVideoEntries(product) {
    /** @type {AirbagVideoEntry[]} */
    const entries = [];

    if (this.dataset.showDefaultIntro === 'true') {
      const src = (this.dataset.defaultIntroSrc || '').trim();
      const title = decodeHtmlEntities(this.dataset.defaultIntroTitle);
      const subtitle = decodeHtmlEntities(this.dataset.defaultIntroSubtitle);
      if (src && title) {
        entries.push({
          href: src,
          title,
          subtitle: subtitle || undefined,
          description: subtitle || undefined,
        });
      }
    }

    this.#pushApiVideos(entries, product.FittingVideos, this.dataset.labelFittingVideo ?? '');
    this.#pushApiVideos(entries, product.InActionVideos, this.dataset.labelInActionVideo ?? '');
    this.#pushApiVideos(entries, product.InstallationVideos, this.dataset.labelInstallationVideo ?? '');

    if (this.dataset.showPressureExtras === 'true') {
      const nr = this.#str(product.ProductNr ?? product.productNr) || this.#currentNr();
      const prefix = nr.slice(0, 2).toUpperCase();
      const heightsLabel = decodeHtmlEntities(this.dataset.labelPressuresHeights);

      if (prefix === 'CR') {
        const t1 = decodeHtmlEntities(this.dataset.pressureCrReferenceTitle);
        const t2 = decodeHtmlEntities(this.dataset.pressureCrOperateTitle);
        if (t1) {
          entries.push({
            href: 'https://www.youtube.com/embed/kXqjb1amGKw',
            title: t1,
            subtitle: heightsLabel || undefined,
            description: heightsLabel || undefined,
          });
        }
        if (t2) {
          entries.push({
            href: 'https://www.youtube.com/embed/rcT1wss4yfQ',
            title: t2,
            subtitle: heightsLabel || undefined,
            description: heightsLabel || undefined,
          });
        }
      } else if (prefix === 'RR' || prefix === 'OA') {
        const t1 = decodeHtmlEntities(this.dataset.pressureRrHeightsTitle);
        const t2 = decodeHtmlEntities(this.dataset.pressureRrOperateTitle);
        if (t1) {
          entries.push({
            href: 'https://www.youtube.com/embed/LHk_KPZPDZQ',
            title: t1,
            subtitle: heightsLabel || undefined,
            description: heightsLabel || undefined,
          });
        }
        if (t2) {
          entries.push({
            href: 'https://www.youtube.com/embed/rcT1wss4yfQ',
            title: t2,
            subtitle: heightsLabel || undefined,
            description: heightsLabel || undefined,
          });
        }
      }
    }

    return entries;
  }

  /**
   * @param {AirbagVideoEntry[]} entries
   */
  #render(entries) {
    this.#entries = entries;

    while (this.refs.tablist.firstChild) this.refs.tablist.removeChild(this.refs.tablist.firstChild);

    if (!entries.length) {
      this.#resetDetail();
      this.classList.remove('airbag-install-videos--single');
      return;
    }

    const isSingle = entries.filter((entry) => entry.href.trim()).length === 1;
    this.classList.toggle('airbag-install-videos--single', isSingle);

    let index = 0;
    for (const entry of entries) {
      const href = entry.href.trim();
      if (!href) continue;

      const tabLabel = decodeHtmlEntities(entry.title) || decodeHtmlEntities(entry.subtitle || '');
      const tabId = `airbag-install-videos-tab-${this.dataset.productId || 'p'}-${index}`;
      const panelId = `airbag-install-videos-panel-${this.dataset.productId || 'p'}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'airbag-install-videos__tab';
      button.id = tabId;
      button.dataset.index = String(index);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.tabIndex = index === 0 ? 0 : -1;

      const label = document.createElement('span');
      label.className = 'airbag-install-videos__tab-label';
      label.textContent = tabLabel;
      button.appendChild(label);

      const tpl = this.refs.arrowIconTemplate;
      if (tpl instanceof HTMLTemplateElement && tpl.content.firstElementChild) {
        button.appendChild(/** @type {Node} */ (tpl.content.firstElementChild.cloneNode(true)));
      }

      if (isSingle) {
        button.classList.add('airbag-install-videos__tab--static');
        button.disabled = true;
      } else {
        const currentIndex = index;
        button.addEventListener('click', () => this.#setActiveIndex(currentIndex));
      }

      this.refs.tablist.appendChild(button);
      index += 1;
    }

    this.refs.detail.id = `airbag-install-videos-panel-${this.dataset.productId || 'p'}`;
    this.refs.detail.setAttribute('role', 'tabpanel');
    this.refs.detail.setAttribute('aria-labelledby', this.refs.tablist.querySelector('.airbag-install-videos__tab')?.id || '');

    const firstEntry = entries.find((item) => item.href.trim());
    if (firstEntry) this.#setActiveIndex(0);
  }

  /**
   * @param {number} index
   */
  #setActiveIndex(index) {
    const entry = this.#entries[index];
    if (!entry) return;

    const tabs = this.refs.tablist.querySelectorAll('.airbag-install-videos__tab');

    for (const [tabIndex, tab] of tabs.entries()) {
      if (!(tab instanceof HTMLButtonElement)) continue;
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle('is-active', isActive);
    }

    const activeTab = tabs[index];
    if (activeTab instanceof HTMLElement) {
      this.refs.detail.setAttribute('aria-labelledby', activeTab.id);
    }

    this.#updateDetail(entry);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onTablistKeydown = (event) => {
    if (this.classList.contains('airbag-install-videos--single')) return;

    const tabs = [...this.refs.tablist.querySelectorAll('.airbag-install-videos__tab')].filter(
      (el) => el instanceof HTMLButtonElement && !el.disabled,
    );
    if (tabs.length < 2) return;

    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    const isVertical = this.#desktopMq?.matches ?? true;

    if (event.key === 'ArrowRight' || (isVertical && event.key === 'ArrowDown')) {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || (isVertical && event.key === 'ArrowUp')) {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!(nextTab instanceof HTMLButtonElement)) return;
    nextTab.focus();
    this.#setActiveIndex(Number(nextTab.dataset.index || nextIndex));
  };

  #resetDetail() {
    this.refs.detailFrameWrap.hidden = true;
    this.refs.detailFrame.src = '';
    this.refs.detailFrame.removeAttribute('title');
    if (this.refs.detailLink) {
      this.refs.detailLink.hidden = true;
      this.refs.detailLink.removeAttribute('href');
      this.refs.detailLink.textContent = '';
      this.refs.detailLink.removeAttribute('target');
      this.refs.detailLink.removeAttribute('rel');
    }
  }

  /**
   * @param {AirbagVideoEntry} entry
   */
  #updateDetail(entry) {
    const href = entry.href.trim();
    if (!href) {
      this.#resetDetail();
      return;
    }

    const decTitle = decodeHtmlEntities(entry.title);
    const decSubtitle = decodeHtmlEntities(entry.subtitle || '');

    const embed = this.dataset.embedVideos === 'true' ? this.#resolveIframeSrc(href) : '';
    if (embed) {
      this.refs.detailFrameWrap.hidden = false;
      this.refs.detailFrame.src = embed;
      this.refs.detailFrame.title = decSubtitle ? `${decTitle} — ${decSubtitle}` : decTitle;
      this.refs.detailFrame.loading = 'lazy';
      this.refs.detailFrame.allowFullscreen = true;
      this.refs.detailFrame.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      );
      if (this.refs.detailLink) this.refs.detailLink.hidden = true;
    } else {
      this.refs.detailFrameWrap.hidden = true;
      this.refs.detailFrame.src = '';
      this.refs.detailFrame.removeAttribute('title');

      const link = this.refs.detailLink;
      if (link) {
        link.hidden = false;
        link.href = href;
        link.textContent = decTitle;
        if (this.dataset.openNewTab === 'true') {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        } else {
          link.removeAttribute('target');
          link.removeAttribute('rel');
        }
      }
    }
  }

  async #load() {
    const nr = this.#currentNr();
    const base = (this.dataset.apiBase || '').trim();
    const requestId = ++this.#requestId;

    this.#showOuterSection();
    this.refs.skeleton.hidden = false;
    this.refs.panel.hidden = true;
    this.setAttribute('aria-busy', 'true');

    if (!nr) {
      return this.#hideOuterSection('missing product number');
    }
    if (!base) {
      return this.#hideOuterSection('missing api base');
    }

    try {
      const data = await fetchAirbagProductDetails({ apiBase: base, productNr: nr });
      if (requestId !== this.#requestId) return;

      const dataRecord = /** @type {Record<string, unknown> | undefined} */ (data);
      const product = /** @type {Record<string, unknown>} */ (dataRecord?.product ?? dataRecord ?? {});
      const entries = this.#buildVideoEntries(product);

      this.#render(entries);

      if (this.refs.tablist.childElementCount === 0) {
        return this.#hideOuterSection('no renderable videos');
      }

      this.refs.skeleton.hidden = true;
      this.refs.panel.hidden = false;
      this.setAttribute('aria-busy', 'false');
    } catch (e) {
      if (requestId !== this.#requestId) return;
      this.#hideOuterSection('fetch error');
    }
  }
}

if (!customElements.get('airbag-install-videos')) {
  customElements.define('airbag-install-videos', AirbagInstallVideos);
}
